'use client'

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { IconChevronDown, IconSend, IconRobot, IconHeadset, IconMessage, IconSparkles } from '@/components/lib/tabler-lucide';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

export default function SiteHelper() {
  const t = useTranslations('Auto.SiteHelperSiteHelper');
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState('ai');
  const [staffInfo, setStaffInfo] = useState(null);
  const [hasBottomAd, setHasBottomAd] = useState(false);
  const scrollRef = useRef(null);
  const pollInterval = useRef(null);

  useEffect(() => {
    const refreshBottomAnchor = () => {
      if (typeof document === 'undefined') return;
      setHasBottomAd(Boolean(document.getElementById('apect-bottom-ad-frame')));
    };

    refreshBottomAnchor();
    const observer = new MutationObserver(refreshBottomAnchor);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', refreshBottomAnchor);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', refreshBottomAnchor);
    };
  }, []);

  useEffect(() => {
    // Generate or retrieve session ID
    const storedSessionId = localStorage.getItem('site_help_session');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadMessages(storedSessionId);
    } else {
      const newSessionId = crypto.randomUUID();
      localStorage.setItem('site_help_session', newSessionId);
      setSessionId(newSessionId);
    }

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    // Poll for new messages when waiting for staff
    if (status === 'staff' && isOpen) {
      pollInterval.current = setInterval(() => {
        if (sessionId) {
          loadMessages(sessionId);
        }
      }, 5000);
    } else if (pollInterval.current) {
      clearInterval(pollInterval.current);
    }

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [status, isOpen, sessionId]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  // Detect the latest staff message info for header display
  useEffect(() => {
    const lastStaffMsg = [...messages].reverse().find(m => m.sender_type === 'staff');
    if (lastStaffMsg) {
      setStaffInfo({
        name: lastStaffMsg.sender_name || t('staff2'),
        avatar: lastStaffMsg.sender_avatar || null,
      });
    }
  }, [messages]);

  async function loadMessages(sid) {
    try {
      const res = await fetch(`/api/site-help?session_id=${sid}`);
      const data = await res.json();
      if (data.messages) {
        setMessages(data.messages);
        setStatus(data.status || 'ai');
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  }

  async function sendMessage() {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    // Optimistically add user message
    setMessages(prev => [...prev, {
      id: Date.now(),
      sender_type: 'user',
      sender_name: 'You',
      content: userMessage,
      created_at: new Date().toISOString()
    }]);

    try {
      const res = await fetch('/api/site-help', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage
        })
      });

      const data = await res.json();

      if (data.ai_response) {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          sender_type: 'ai',
          sender_name: 'Gary (AI)',
          content: data.ai_response,
          created_at: new Date().toISOString()
        }]);
      }

      if (data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem('site_help_session', data.session_id);
      }

    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setLoading(false);
    }
  }

  async function requestStaff() {
    setLoading(true);
    try {
      const res = await fetch('/api/site-help', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          action: 'request_staff'
        })
      });

      const data = await res.json();
      setStatus(t('staff'));
      setStaffInfo(null);

      setMessages(prev => [...prev, {
        id: Date.now(),
        sender_type: 'system',
        sender_name: 'System',
        content: data.message || t('staffHasBeenNotified'),
        created_at: new Date().toISOString()
      }]);

    } catch (error) {
      console.error('Failed to request staff:', error);
    } finally {
      setLoading(false);
    }
  }

  function startNewChat() {
    const newSessionId = crypto.randomUUID();
    localStorage.setItem('site_help_session', newSessionId);
    setSessionId(newSessionId);
    setMessages([]);
    setStatus('ai');
    setStaffInfo(null);
  }

  // Determine header display
  function getHeaderInfo() {
    if (status === 'staff' && staffInfo) {
      return {
        name: staffInfo.name,
        subtitle: '👤 Staff Member',
        avatar: staffInfo.avatar,
      };
    }
    if (status === 'staff') {
      return {
        name: t('apectSupport'),
        subtitle: '⏳ Waiting for staff...',
        avatar: '/logo.png',
      };
    }
    return {
      name: t('garyAi'),
      subtitle: null,
      avatar: '/logo.png',
    };
  }

  const headerInfo = getHeaderInfo();

  return (
    <div
      className="fixed right-6 z-[180] flex flex-col items-end gap-2 max-sm:right-4"
      style={{ bottom: hasBottomAd ? 112 : 24 }}
    >
      {/* Chat window */}
      {isOpen && (
        <Card className="w-[360px] shadow-xl border-0 flex flex-col overflow-hidden origin-bottom-right animate-in fade-in-0 zoom-in-90 slide-in-from-bottom-4 duration-300 ease-out"
          style={{ height: 450 }}>
          {/* Header */}
          <CardHeader className="px-3 py-2.5 border-b border-border/60 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="/logo.png" alt="support" />
                  <AvatarFallback>
                    {status === 'ai' ? <IconRobot size={14} /> : <IconHeadset size={14} />}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold">{headerInfo.name}</span>
                    {status === 'ai' && (
                      <Badge variant="secondary" className="h-4 text-[10px] px-1.5 gap-0.5">
                        <IconSparkles size={8} />
                        {t('ai')}
                      </Badge>
                    )}
                  </div>
                  {headerInfo.subtitle && (
                    <p className="text-xs text-muted-foreground">{headerInfo.subtitle}</p>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={startNewChat}>
                {t('newChat')}
              </Button>
            </div>
          </CardHeader>

          {/* Messages */}
          <ScrollArea className="flex-1 px-3 py-2.5">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
                <Avatar className="h-14 w-14">
                  <AvatarImage src="/logo.png" alt={t('gary')} />
                  <AvatarFallback><IconRobot size={24} /></AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-sm">{t('welcomeToApectSupport')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('ourAiAssistantGary')}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 rounded-xl"
                  onClick={requestStaff}
                  disabled={loading}
                >
                  <IconHeadset size={14} />
                  {t('askARealStaff')}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {messages.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className={`flex gap-2 ${msg.sender_type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    {msg.sender_type !== 'user' && (
                      <Avatar className="h-6 w-6 flex-shrink-0">
                        <AvatarImage
                          src={msg.sender_type === 'ai' ? '/logo.png' : msg.sender_avatar}
                          alt={msg.sender_name}
                        />
                        <AvatarFallback className="text-[10px]">
                          {msg.sender_type === 'staff' ? <IconHeadset size={10} /> : <IconRobot size={10} />}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className={`flex flex-col gap-0.5 max-w-[75%] ${msg.sender_type === 'user' ? 'items-end' : 'items-start'}`}>
                      {msg.sender_type !== 'user' && (
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground font-medium">{msg.sender_name}</span>
                          {msg.sender_type === 'ai' && (
                            <Badge variant="secondary" className="h-3.5 text-[9px] px-1">
                              <IconSparkles size={7} />
                            </Badge>
                          )}
                        </div>
                      )}
                      <div className={`rounded-2xl px-3 py-2 text-sm ${
                        msg.sender_type === 'user'
                          ? 'bg-primary text-primary-foreground rounded-tr-sm'
                          : msg.sender_type === 'system'
                          ? 'bg-muted text-muted-foreground italic text-xs'
                          : 'bg-muted text-foreground rounded-tl-sm'
                      }`}>
                        {msg.content}
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="text-xs">{t('typing')}</span>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Ask staff banner */}
          {status === 'ai' && messages.length > 0 && (
            <>
              <Separator />
              <div className="px-3 py-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full h-7 rounded-xl text-xs gap-1.5 text-muted-foreground"
                  onClick={requestStaff}
                  disabled={loading}
                >
                  <IconHeadset size={12} />
                  {t('askARealStaff')}
                </Button>
              </div>
            </>
          )}

          {/* Input */}
          <Separator />
          <div className="px-3 py-2 flex gap-2">
            <Input
              placeholder={t('typeYourMessage')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              disabled={loading}
              className="h-8 text-sm"
            />
            <Button
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={sendMessage}
              disabled={!input.trim() || loading}
            >
              <IconSend size={14} />
            </Button>
          </div>
        </Card>
      )}

      {/* Toggle button */}
      <Button
        size="icon"
        className="h-12 w-12 rounded-full shadow-lg"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <IconChevronDown size={20} />
        ) : (
          <IconMessage size={20} />
        )}
      </Button>
    </div>
  );
}

